import { authorizedFetch, json } from '@nimbitlabs/ui';

export type RunbookAction = 'restart' | 'scale' | 'deploy' | 'rollback' | 'custom' | 'webhook';
export type IncidentSeverity = 'sev1' | 'sev2' | 'sev3' | 'sev4';
export type IncidentStatus = 'investigating' | 'identified' | 'monitoring' | 'resolved';

export interface RunbookStep {
  id: string;
  order: number;
  title: string;
  description: string;
  action?: RunbookAction;
  actionConfig?: Record<string, any>;
  automated: boolean;
}

export interface Runbook {
  id: string;
  name: string;
  description: string;
  serviceId: string;
  serviceName: string;
  trigger: string;
  steps: RunbookStep[];
  lastUsed?: Date;
  usageCount: number;
  createdAt: Date;
}

export interface Incident {
  id: string;
  title: string;
  description: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  serviceId: string;
  serviceName: string;
  environmentId: string;
  environmentName: string;
  assignedTo?: string;
  assignedToName?: string;
  runbookId?: string;
  startedAt: Date;
  resolvedAt?: Date;
  timeline: IncidentTimelineEvent[];
}

export interface IncidentTimelineEvent {
  id: string;
  timestamp: Date;
  userId: string;
  userName: string;
  type: 'status_change' | 'comment' | 'action_taken';
  content: string;
}

// Date parsing helpers
const parseRunbookDate = (data: any): Runbook => ({
  ...data,
  lastUsed: data.lastUsed ? new Date(data.lastUsed) : undefined,
  createdAt: new Date(data.createdAt),
});

const parseIncidentDate = (data: any): Incident => ({
  ...data,
  startedAt: new Date(data.startedAt),
  resolvedAt: data.resolvedAt ? new Date(data.resolvedAt) : undefined,
  timeline: data.timeline?.map((event: any) => ({
    ...event,
    timestamp: new Date(event.timestamp),
  })) || [],
});

export const runbooksApi = {
  // Runbooks
  async getRunbooks(serviceId?: string): Promise<Runbook[]> {
    const params = serviceId ? `?serviceId=${serviceId}` : '';
    const res = await authorizedFetch(`/runbooks${params}`);
    const data = await json<any[]>(res);
    return data.map(parseRunbookDate);
  },

  async getRunbook(id: string): Promise<Runbook> {
    const res = await authorizedFetch(`/runbooks/${id}`);
    const data = await json<any>(res);
    return parseRunbookDate(data);
  },

  async createRunbook(runbook: Omit<Runbook, 'id' | 'usageCount' | 'createdAt'>): Promise<Runbook> {
    const res = await authorizedFetch('/runbooks', {
      method: 'POST',
      body: JSON.stringify(runbook),
    });
    const data = await json<any>(res);
    return parseRunbookDate(data);
  },

  async updateRunbook(id: string, updates: Partial<Runbook>): Promise<Runbook> {
    const res = await authorizedFetch(`/runbooks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    const data = await json<any>(res);
    return parseRunbookDate(data);
  },

  async deleteRunbook(id: string): Promise<void> {
    await authorizedFetch(`/runbooks/${id}`, {
      method: 'DELETE',
    });
  },

  async executeRunbook(id: string, incidentId?: string): Promise<void> {
    await authorizedFetch(`/runbooks/${id}/execute`, {
      method: 'POST',
      body: JSON.stringify({ incidentId }),
    });
  },

  // Incidents
  async getIncidents(status?: IncidentStatus, severity?: IncidentSeverity): Promise<Incident[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (severity) params.set('severity', severity);
    
    const queryString = params.toString();
    const url = `/incident-management${queryString ? `?${queryString}` : ''}`;
    
    const res = await authorizedFetch(url);
    const data = await json<any[]>(res);
    return data.map(parseIncidentDate);
  },

  async getIncident(id: string): Promise<Incident> {
    const res = await authorizedFetch(`/incident-management/${id}`);
    const data = await json<any>(res);
    return parseIncidentDate(data);
  },

  async createIncident(incident: Omit<Incident, 'id' | 'startedAt' | 'timeline'>): Promise<Incident> {
    const res = await authorizedFetch('/incident-management', {
      method: 'POST',
      body: JSON.stringify(incident),
    });
    const data = await json<any>(res);
    return parseIncidentDate(data);
  },

  async updateIncident(id: string, updates: Partial<Incident>): Promise<Incident> {
    const res = await authorizedFetch(`/incident-management/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    const data = await json<any>(res);
    return parseIncidentDate(data);
  },

  async addIncidentComment(id: string, userId: string, userName: string, comment: string): Promise<void> {
    await authorizedFetch(`/incident-management/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ userId, userName, comment }),
    });
  },
};
